import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, FileX2, Vote, Building2, Sparkle } from "lucide-react";
import { categorySlug } from "../lib/seo";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/0919926a-1f4b-48e1-856e-b5a2d49c95a0/images/ef864a913a2b988972837e0f419a729f8d7de808d21d21d1f0ac351df771f1a4.png";
const VOTING_IMG = "https://static.prod-images.emergentagent.com/jobs/0919926a-1f4b-48e1-856e-b5a2d49c95a0/images/74683b4c5998aef6918f24cb2506221546ea5801382b6a7b1ef5a087b20cde9f.png";
const ENV_IMG = "https://static.prod-images.emergentagent.com/jobs/0919926a-1f4b-48e1-856e-b5a2d49c95a0/images/ccd78a16988bd2842f68e6903a36613e3c5dee0e98e6041989001d086fde2c1f.png";

const CATEGORIES = [
  { name: "Transport",      desc: "Mobilitate, piste, transport public" },
  { name: "Mediu",          desc: "Spații verzi, calitatea aerului, deșeuri" },
  { name: "Educație",       desc: "Școli, burse, infrastructură școlară" },
  { name: "Sănătate",       desc: "Cabinete, spitale, prevenție" },
  { name: "Infrastructură", desc: "Drumuri, poduri, rețele de utilități" },
  { name: "Digitalizare",   desc: "Servicii publice 100% online, transparență" },
];

export default function Landing({ onOpenAuth }) {
  return (
    <main>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <img
          src={HERO_BG}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-25 pointer-events-none"
        />
        <div className="hero-pattern absolute inset-0 pointer-events-none" />
        <div className="container-civic relative pt-20 pb-24 md:pt-28 md:pb-32 grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary/30 text-xs uppercase tracking-[0.2em] font-semibold text-primary bg-white/70 backdrop-blur">
              <FileX2 size={14} /> Civic-tech · România
            </div>
            <h1 className="mt-6 font-display text-5xl md:text-7xl font-black tracking-[-0.03em] leading-[0.95]">
              Proiectele bune <span className="text-[#00D670]">ajung</span><br />
              la primărie. <span className="italic font-medium">Fără hârtie.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-base md:text-lg leading-relaxed text-foreground/75">
              fără hârtie.ro este platforma cetățenească unde locuitorii votează direct
              proiectele dedicate autorităților locale. Mai mult Cluj cu piste, mai mult
              Iași cu cabinete medicale, mai mult Timișoara cu parcuri – tu decizi ce
              merge mai departe.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/proiecte" className="btn-primary" data-testid="cta-projects">
                Vezi proiectele <ArrowUpRight size={16} />
              </Link>
              <button onClick={() => onOpenAuth?.("register")} className="btn-secondary" data-testid="cta-register">
                Creează cont
              </button>
            </div>
          </div>

          <div className="md:col-span-4 hidden md:block">
            <div className="card-flat p-6 bg-white">
              <div className="label-overline">Astăzi</div>
              <p className="font-display text-4xl font-extrabold mt-2 tabular-nums">9</p>
              <p className="text-sm text-foreground/70">proiecte deschise la vot</p>
              <hr className="my-4 border-border" />
              <div className="flex items-center gap-3">
                <Vote className="text-[#0A4D34]" size={20} />
                <span className="text-sm">1 vot · 1 cetățean · 0 hârtii</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="container-civic py-20">
        <div className="grid md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <div className="label-overline">Cum funcționează</div>
            <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight mt-3">
              Trei pași.<br />Zero birocrație.
            </h2>
          </div>
          <div className="md:col-span-8 grid sm:grid-cols-3 gap-0 border-l border-t border-border">
            {[
              { n: "01", t: "Te conectezi", d: "Cu Google sau email. Un cont, un vot per proiect." },
              { n: "02", t: "Votezi proiecte", d: "Susții sau respingi proiecte locale, filtrate după județ și categorie." },
              { n: "03", t: "Ajungem la primărie", d: "Lunar, livrăm topul către autoritățile vizate. Public, transparent." },
            ].map((s) => (
              <div key={s.n} className="border-r border-b border-border p-8 bg-white">
                <p className="font-display text-3xl font-black text-[#00D670]">{s.n}</p>
                <h3 className="font-display text-xl font-bold mt-3">{s.t}</h3>
                <p className="text-sm text-foreground/70 mt-2 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="container-civic py-16 border-t border-border">
        <div className="flex items-end justify-between gap-6 flex-wrap mb-10">
          <div>
            <div className="label-overline">Categorii</div>
            <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight mt-3">
              Șase domenii. Un singur loc.
            </h2>
          </div>
          <Link to="/proiecte" className="text-sm font-semibold underline underline-offset-4 hover:text-primary" data-testid="all-projects-link">
            Toate proiectele →
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0 border-t border-l border-border">
          {CATEGORIES.map((c) => (
            <Link
              key={c.name}
              to={`/proiecte/${categorySlug(c.name)}`}
              className="border-r border-b border-border p-8 bg-white group hover:bg-secondary transition-colors"
              data-testid={`category-card-${c.name}`}
            >
              <div className="flex items-center justify-between">
                <h3 className="font-display text-2xl font-bold tracking-tight">{c.name}</h3>
                <ArrowUpRight className="text-foreground/40 group-hover:text-primary transition-colors" size={20} />
              </div>
              <p className="text-sm text-foreground/70 mt-3">{c.desc}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* MANIFEST */}
      <section className="bg-primary text-primary-foreground mt-24">
        <div className="container-civic py-20 md:py-28 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#00D670]">Manifest</div>
            <h2 className="font-display text-4xl md:text-6xl font-black tracking-tight mt-4 leading-[1.02]">
              Birocrația alege fereastra.<br />
              <span className="text-[#00D670]">Cetățeanul</span> alege priorități.
            </h2>
            <p className="mt-6 max-w-xl text-base text-primary-foreground/80 leading-relaxed">
              Listele de la sertar nu mai sunt suficiente. Vrem un proces deschis,
              ușor de înțeles și ușor de verificat – în care fiecare proiect are un
              autor, un buget estimativ și un destinatar clar.
            </p>
          </div>
          <div className="md:col-span-5">
            <img src={VOTING_IMG} alt="" className="w-full rounded-sm border border-white/10" />
          </div>
        </div>
      </section>

      {/* GREEN BAND */}
      <section className="container-civic py-20 grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-6 order-2 md:order-1">
          <img src={ENV_IMG} alt="" className="w-full rounded-sm border border-border" />
        </div>
        <div className="md:col-span-6 order-1 md:order-2">
          <div className="label-overline">De ce „fără hârtie"</div>
          <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight mt-3">
            Mai puține dosare. Mai multe decizii bune.
          </h2>
          <ul className="mt-6 space-y-4 text-sm text-foreground/75">
            <li className="flex gap-3"><Sparkle className="text-[#00D670] shrink-0" size={18}/> Toate proiectele într-un singur format public, comparabil.</li>
            <li className="flex gap-3"><Building2 className="text-[#00D670] shrink-0" size={18}/> Fiecare proiect are o autoritate locală destinatară.</li>
            <li className="flex gap-3"><Vote className="text-[#00D670] shrink-0" size={18}/> Voturile sunt agregate lunar și trimise public către primării.</li>
          </ul>
          <Link to="/proiecte" className="btn-primary mt-8 inline-flex" data-testid="manifest-cta">
            Începe să votezi
          </Link>
        </div>
      </section>
    </main>
  );
}
